/**
 * Web Vitals Reporting
 *
 * Monitors Core Web Vitals (CLS, FCP, LCP, TTFB) and reports them
 * in batches. In production, metrics are sent to /api/metrics/vitals
 * every 10 seconds or on page hide. In development, metrics are
 * logged to the console as a table.
 *
 * Input: web-vitals library metric events
 * Output: batched POST to /api/metrics/vitals (production) or console.table (dev)
 */

import { onCLS, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';

interface MetricEntry {
  name: string;
  value: number;
  rating: string;
}

const metricsQueue: MetricEntry[] = [];

/**
 * Flush queued metrics to the reporting endpoint or console.
 * In production, sends a POST request with the batch.
 * In development, logs a table to the console.
 */
function sendMetrics(): void {
  if (metricsQueue.length === 0) return;

  const batch = [...metricsQueue];
  metricsQueue.length = 0;

  if (import.meta.env.PROD) {
    fetch('/api/metrics/vitals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        metrics: batch,
        timestamp: new Date().toISOString(),
        url: window.location.pathname,
      }),
    }).catch(() => {
      // fire-and-forget: silently ignore reporting failures
    });
  } else {
    console.table(batch);
  }
}

/**
 * Queue a single metric for batch reporting.
 */
function reportMetric(metric: Metric): void {
  metricsQueue.push({
    name: metric.name,
    value: Math.round(metric.value * 100) / 100,
    rating: metric.rating,
  });
}

// Flush metrics every 10 seconds
setInterval(sendMetrics, 10_000);

// Also flush on page hide (user navigating away or switching tabs)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    sendMetrics();
  }
});

/**
 * Initialize Web Vitals monitoring.
 * Registers handlers for CLS, FCP, LCP, and TTFB metrics.
 */
export function initWebVitals(): void {
  onCLS(reportMetric);
  onFCP(reportMetric);
  onLCP(reportMetric);
  onTTFB(reportMetric);
}
